import { Autocomplete, Box, Typography } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { WidgetProps } from '@rjsf/utils';
import { colors } from '@src/styles';
import { useReferenceLookup } from './referenceLookupContext';
import {
  ReferenceOption,
  useOrganizationOptions,
  useOrganizationWorkflows,
  useWorkflowPhases,
} from './useWorkflowReferences';

const monoFontFamily = "'JetBrains Mono', monospace";

export type ReferenceKind = 'workflow' | 'phase' | 'organization';

interface ReferenceStatus {
  tone: 'ok' | 'warn' | 'muted';
  text: string;
}

interface ReferenceSource {
  options: ReferenceOption[];
  isLoading: boolean;
  isError: boolean;
  blockedReason?: string;
}

const KIND_NOUNS: Record<ReferenceKind, { plural: string; article: string; empty: string }> = {
  workflow: { plural: 'workflows', article: 'o workflow', empty: 'Nenhum workflow encontrado' },
  phase: { plural: 'fases', article: 'a fase', empty: 'Nenhuma fase encontrada' },
  organization: { plural: 'organizações', article: 'a organização', empty: 'Nenhuma organização encontrada' },
};

const TONE_COLORS: Record<ReferenceStatus['tone'], string> = {
  ok: colors.brand.textStrong,
  warn: colors.status.pending,
  muted: colors.brand.textMuted,
};

const dropdownPaperSX = {
  marginTop: 0.5,
  backgroundColor: colors.brand.surface,
  backgroundImage: 'none',
  border: `1px solid ${colors.brand.line}`,
  borderRadius: '10px',
  boxShadow: '0 16px 40px rgba(0, 0, 0, 0.45)',
  color: colors.primary.white,
  '& .MuiAutocomplete-listbox': { padding: 0.5, maxHeight: 280 },
  '& .MuiAutocomplete-option': {
    borderRadius: '6px',
    '&.Mui-focused': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
    '&[aria-selected="true"]': { backgroundColor: 'rgba(255, 160, 21, 0.12)' },
  },
  '& .MuiAutocomplete-noOptions, & .MuiAutocomplete-loading': { color: colors.brand.textSecondary, fontSize: '12.5px' },
} as const;

const matchesInput = (option: ReferenceOption, input: string): boolean => {
  const term = input.trim().toLowerCase();
  if (!term) return true;
  return option.label.toLowerCase().includes(term) || option.id.toLowerCase().includes(term);
};

const useReferenceSource = (kind: ReferenceKind): ReferenceSource => {
  const { organizationId, workflowId } = useReferenceLookup();
  const { workflows, phases, isKnownWorkflow } = useWorkflowPhases(
    kind === 'organization' ? undefined : organizationId,
    kind === 'phase' ? workflowId : undefined,
  );
  const organizations = useOrganizationOptions(kind === 'organization');

  if (kind === 'organization') {
    return {
      options: organizations.data ?? [],
      isLoading: organizations.isLoading,
      isError: organizations.isError,
    };
  }
  if (!organizationId) {
    return {
      options: [],
      isLoading: false,
      isError: false,
      blockedReason: 'Selecione a organização para buscar pelo nome.',
    };
  }
  if (kind === 'workflow') {
    return { options: workflows.data ?? [], isLoading: workflows.isLoading, isError: workflows.isError };
  }
  if (!workflowId) {
    return { options: [], isLoading: false, isError: false, blockedReason: 'Informe o workflow para buscar as fases.' };
  }
  if (workflows.isLoading || (isKnownWorkflow && phases.isLoading)) {
    return { options: [], isLoading: true, isError: false };
  }
  if (workflows.isError || phases.isError) return { options: [], isLoading: false, isError: true };
  if (!isKnownWorkflow) {
    return {
      options: [],
      isLoading: false,
      isError: false,
      blockedReason: 'O workflow informado não está nesta organização.',
    };
  }
  return { options: phases.data ?? [], isLoading: false, isError: false };
};

const getStatus = (
  kind: ReferenceKind,
  value: string,
  source: ReferenceSource,
  match?: ReferenceOption,
): ReferenceStatus => {
  const nouns = KIND_NOUNS[kind];
  if (source.blockedReason) return { tone: 'muted', text: source.blockedReason };
  if (source.isLoading) return { tone: 'muted', text: `Buscando ${nouns.plural}…` };
  if (source.isError) return { tone: 'muted', text: `Não foi possível consultar ${nouns.plural}.` };
  if (match) return { tone: 'ok', text: match.label };
  if (value) return { tone: 'warn', text: `Não encontrei ${nouns.article} com esse ID.` };
  return { tone: 'muted', text: 'Busque pelo nome ou cole o ID.' };
};

export const ReferenceLookupWidget = ({ kind, ...props }: WidgetProps & { kind: ReferenceKind }) => {
  const source = useReferenceSource(kind);
  const value = typeof props.value === 'string' ? props.value : '';
  const match = source.options.find((option) => option.id === value);
  const status = getStatus(kind, value, source, match);
  const isEditable = !props.readonly && !props.disabled;
  const nouns = KIND_NOUNS[kind];
  const StatusIcon =
    status.tone === 'ok' ? CheckCircleRoundedIcon : status.tone === 'warn' ? ErrorOutlineRoundedIcon : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%' }}>
      {isEditable ? (
        <Autocomplete<ReferenceOption, false, false, true>
          freeSolo
          options={source.options}
          inputValue={value}
          value={match ?? null}
          onInputChange={(_event, nextValue, reason) => {
            if (reason === 'input' || reason === 'clear') props.onChange(nextValue.trim() || undefined);
          }}
          onChange={(_event, option) => {
            if (option && typeof option !== 'string') props.onChange(option.id);
          }}
          filterOptions={(list, state) =>
            match ? list : list.filter((option) => matchesInput(option, state.inputValue))
          }
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.id)}
          isOptionEqualToValue={(option, selected) => option.id === selected.id}
          loading={source.isLoading}
          loadingText={`Buscando ${nouns.plural}…`}
          noOptionsText={source.blockedReason ? '' : nouns.empty}
          slotProps={{ paper: { sx: dropdownPaperSX } }}
          renderOption={(optionProps, option) => {
            const { key, ...rest } = optionProps as typeof optionProps & { key: string };
            return (
              <Box component="li" key={key} {...rest}>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography noWrap sx={{ fontSize: '13px', color: colors.primary.white, flex: 1, minWidth: 0 }}>
                      {option.label}
                    </Typography>
                    {option.detail && (
                      <Typography sx={{ fontFamily: monoFontFamily, fontSize: '10px', color: colors.brand.textMuted }}>
                        {option.detail}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    noWrap
                    sx={{ fontFamily: monoFontFamily, fontSize: '10.5px', color: colors.brand.textMuted }}
                  >
                    {option.id}
                  </Typography>
                </Box>
              </Box>
            );
          }}
          renderInput={(params) => (
            <Box ref={params.InputProps.ref} sx={{ display: 'flex', width: '100%' }}>
              <input
                {...params.inputProps}
                id={props.id}
                placeholder={
                  source.blockedReason ? props.placeholder : `Busque ${nouns.article} pelo nome ou cole o ID`
                }
                required={props.required}
                autoFocus={props.autofocus}
                onBlur={(event) => {
                  params.inputProps.onBlur?.(event);
                  props.onBlur?.(props.id, event.target.value || undefined);
                }}
                style={{ flex: 1, fontFamily: monoFontFamily, fontSize: '12.5px' }}
              />
            </Box>
          )}
        />
      ) : (
        <input
          id={props.id}
          type="text"
          value={value}
          readOnly
          style={{ flex: 1, fontFamily: monoFontFamily, fontSize: '12.5px' }}
        />
      )}
      <Box
        aria-live="polite"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minHeight: 18, color: TONE_COLORS[status.tone] }}
      >
        {StatusIcon && (
          <StatusIcon
            sx={{
              fontSize: 15,
              color: `${status.tone === 'ok' ? colors.status.concluded : colors.status.pending} !important`,
            }}
          />
        )}
        <Typography
          sx={{ fontSize: status.tone === 'ok' ? '13px' : '11.5px', fontWeight: status.tone === 'ok' ? 500 : 400 }}
        >
          {status.text}
        </Typography>
      </Box>
    </Box>
  );
};

export const WorkflowReferenceWidget = (props: WidgetProps) => <ReferenceLookupWidget {...props} kind="workflow" />;

export const PhaseReferenceWidget = (props: WidgetProps) => <ReferenceLookupWidget {...props} kind="phase" />;

export const OrganizationReferenceWidget = (props: WidgetProps) => (
  <ReferenceLookupWidget {...props} kind="organization" />
);

export const useOrganizationWorkflowNames = (organizationId?: string): Record<string, string> => {
  const workflows = useOrganizationWorkflows(organizationId);
  return Object.fromEntries((workflows.data ?? []).map((workflow) => [workflow.id, workflow.label]));
};
